import { AddCircle, ContentCopy, HistoryOutlined } from "@mui/icons-material";
import { Divider, Stack } from "@mui/material";
import { ChatMessageWithParts, ContentType } from "src/views/assistant/types";

import ChatShareButton from "../ChatShareButton";
import { useHistory } from "react-router-dom";
import { convertMarkdownToHtml } from "src/helpers/markdownUtils";
import { NucToast } from "src/reusable/components/Toast";
import AAHeaderButton, { useIsHeaderCompact } from "./AAHeaderButton";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { useIsDialogueOwner } from "src/views/assistant/hooks/useIsDialogueOwner";

type DialogueActionsProps = {
  dialogueId: number | null;
  onNewChat: () => void;
  dialogue: ChatMessageWithParts[];
};

const DialogueActions = ({
  dialogueId,
  onNewChat,
  dialogue,
}: DialogueActionsProps) => {
  const history = useHistory();
  const isMobile = useIsHeaderCompact();
  const { basePath } = useChatStore((state) => state.productConfig);

  // Resuming an existing chat, so an unloaded dialogue is not yet known to be
  // this user's — the default.
  const isOwner = useIsDialogueOwner(dialogueId);

  const sanitizedBasePath = basePath.endsWith("/")
    ? basePath.slice(0, -1)
    : basePath;

  const navigateToHistoryPage = () => {
    history.push(`${sanitizedBasePath}/recents`);
  };

  const formatDialogueForExport = (
    dialogue: ChatMessageWithParts[],
  ): string => {
    return dialogue
      .filter((msg) => msg.type === "User" || msg.type === "Assistant")
      .map((msg) => {
        const content = msg.contentParts
          .filter((part) => part.type === ContentType.TEXT && part.text.trim())
          .map((part) => part.text.trim())
          .join("");

        if (!content) return null;
        const label = msg.type === "User" ? "User:" : "Assistant:";
        return `### ${label}\n\n${content}`;
      })
      .filter(Boolean)
      .join("\n\n");
  };

  const handleCopyDialogue = async () => {
    if (!dialogueId || !dialogue.length) return;
    try {
      const dialogueText = formatDialogueForExport(dialogue);
      const htmlDialogueText = await convertMarkdownToHtml(dialogueText);
      const data = new ClipboardItem({
        "text/plain": new Blob([dialogueText], { type: "text/plain" }),
        "text/html": new Blob([htmlDialogueText], { type: "text/html" }),
      });
      await navigator.clipboard.write([data]);
      NucToast({
        message: "Dialogue copied to clipboard.",
        type: "success",
      });
    } catch (error) {
      console.error("Failed to copy dialogue:", error);
      const message = navigator.clipboard
        ? "Failed to copy dialogue."
        : "Clipboard API not supported in this browser.";
      NucToast({ message, type: "error" });
    }
  };

  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <AAHeaderButton
        icon={<HistoryOutlined />}
        label="History"
        onClick={navigateToHistoryPage}
        tooltip="History"
        variant="outlined"
        isMobile={isMobile}
        data-testid="aa-chat-history-btn"
        data-attr="assistant:btn:view-chat-history"
      />
      {dialogueId && isOwner && (
        <ChatShareButton dialogueId={dialogueId} isMobile={isMobile} />
      )}
      {dialogueId && (
        <AAHeaderButton
          icon={<ContentCopy />}
          label="Copy"
          onClick={handleCopyDialogue}
          tooltip="Copy dialogue to clipboard"
          variant="outlined"
          isMobile={isMobile}
          data-testid="aa-copy-dialogue-btn"
          data-attr="assistant:btn:copy-dialogue"
        />
      )}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 0.75 }} />
      <AAHeaderButton
        icon={<AddCircle />}
        label="New chat"
        onClick={onNewChat}
        tooltip="New chat"
        variant="contained"
        isMobile={isMobile}
        data-testid="aa-new-chat-btn"
        data-attr="assistant:btn:new-chat"
      />
    </Stack>
  );
};

export default DialogueActions;
